import { useEffect, useState, type FormEvent } from "react";
import { supabase } from "../lib/supabase.js";
import { callFunction } from "../lib/callFunction.js";

interface SettingsRow {
  issuer_name: string;
  abn: string;
  address: string;
  payment_instructions: string;
  due_days: number;
  auto_send: boolean;
  auto_send_usage: boolean;
  alert_email: string;
}

const FIELD: React.CSSProperties = { display: "block", width: "100%", padding: 8, marginTop: 4, boxSizing: "border-box" };

/**
 * Billing: the single billing_settings row (migration 0025): who the invoice is from, how the client pays (no payment
 * collection, so this is just text on the PDF), and whether invoices are emailed automatically after the
 * scheduled run. Auto-send is OFF until switched on here, so the first scheduled runs only create drafts.
 */
function BillingSection() {
  const [s, setS] = useState<SettingsRow | null>(null);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState<string | null>(null);
  const [alertMessage, setAlertMessage] = useState<string | null>(null);

  useEffect(() => {
    supabase
      .from("billing_settings")
      .select("issuer_name, abn, address, payment_instructions, due_days, auto_send, auto_send_usage, alert_email")
      .eq("id", true)
      .single()
      .then(({ data }) => setS(data as SettingsRow | null));
  }, []);

  function set<K extends keyof SettingsRow>(key: K, value: SettingsRow[K]) {
    setS((prev) => (prev ? { ...prev, [key]: value } : prev));
  }

  async function save(e: FormEvent) {
    e.preventDefault();
    if (!s) return;
    setSaving(true);
    setMessage(null);
    const { error } = await supabase.from("billing_settings").update(s).eq("id", true);
    setSaving(false);
    setMessage(error ? error.message : "Saved.");
  }

  async function sendTestAlert() {
    setAlertMessage("Sending...");
    const res = await callFunction<{ sent?: boolean }>("billing-alert", { test: true });
    setAlertMessage(
      res.ok
        ? "Test email sent."
        : res.error === "alert_email_not_set"
          ? "Save an alert email first."
          : res.error === "billing_settings_incomplete"
            ? "Fill in the from address first."
            : res.error
    );
  }

  if (!s) return <p className="rc-loading">Loading...</p>;

  const incomplete = !s.issuer_name.trim() || !s.abn.trim();

  return (
    <form onSubmit={save}>
      <h2 className="rc-h2" style={{ marginBottom: 8 }}>Billing</h2>
      <p style={{ fontSize: 13, color: "var(--rc-text-muted)", marginTop: 0 }}>
        These appear on every invoice PDF and email. Invoices are not sent until the name, ABN and from address are filled in.
      </p>
      {incomplete && <p style={{ color: "var(--rc-warning)", fontSize: 14 }}>Incomplete: name and ABN are required (and the from address under Communication) before any invoice can be sent.</p>}

      <label style={{ display: "block", marginBottom: 12, fontSize: 14 }}>
        Issuer name
        <input value={s.issuer_name} onChange={(e) => set("issuer_name", e.target.value)} style={FIELD} />
      </label>
      <label style={{ display: "block", marginBottom: 12, fontSize: 14 }}>
        ABN
        <input value={s.abn} onChange={(e) => set("abn", e.target.value)} style={FIELD} />
      </label>
      <label style={{ display: "block", marginBottom: 12, fontSize: 14 }}>
        Address (one line per row)
        <textarea value={s.address} onChange={(e) => set("address", e.target.value)} rows={3} style={FIELD} />
      </label>
      <p style={{ fontSize: 13, color: "var(--rc-text-muted)", margin: "0 0 12px" }}>
        The from and reply-to addresses are set under Communication, Email.
      </p>
      <label style={{ display: "block", marginBottom: 12, fontSize: 14 }}>
        How to pay (bank details, reference, terms)
        <textarea value={s.payment_instructions} onChange={(e) => set("payment_instructions", e.target.value)} rows={4} style={FIELD} />
      </label>
      <label style={{ display: "block", marginBottom: 12, fontSize: 14 }}>
        Days until due
        <input type="number" min={0} value={s.due_days} onChange={(e) => set("due_days", Math.max(0, Number(e.target.value) || 0))} style={{ ...FIELD, width: 120 }} />
      </label>
      <label style={{ display: "flex", gap: 8, alignItems: "center", marginBottom: 8, fontSize: 14 }}>
        <input type="checkbox" checked={s.auto_send} onChange={(e) => set("auto_send", e.target.checked)} />
        Email base-fee invoices to clients automatically after the scheduled run (otherwise they stay as drafts until sent from the Invoices screen)
      </label>
      <label style={{ display: "flex", gap: 8, alignItems: "center", marginBottom: 4, fontSize: 14 }}>
        <input type="checkbox" checked={s.auto_send_usage} onChange={(e) => set("auto_send_usage", e.target.checked)} />
        Email usage invoices automatically too
      </label>
      <p style={{ fontSize: 12, color: "var(--rc-text-faint)", margin: "0 0 16px 24px" }}>
        Usage invoices come from the carrier usage sync. Leave this off until you have checked the usage figures; they are then created as drafts for you to review and send.
      </p>

      <label style={{ display: "block", marginBottom: 6, fontSize: 14 }}>
        Alert email (blank = no alerts)
        <input type="email" value={s.alert_email} onChange={(e) => set("alert_email", e.target.value)} style={FIELD} />
      </label>
      <p style={{ fontSize: 12, color: "var(--rc-text-faint)", margin: "0 0 8px" }}>
        Emailed once a day when the usage sync fails or goes stale, a billing job is blocked, or an invoice fails to send or bounces. Save first, then test.
      </p>
      <button type="button" onClick={() => void sendTestAlert()} style={{ padding: "6px 12px", marginBottom: 20 }}>
        Send test alert
      </button>
      {alertMessage && <span style={{ marginLeft: 12, fontSize: 14 }}>{alertMessage}</span>}
      <br />

      <button type="submit" disabled={saving} style={{ padding: "8px 16px" }}>
        {saving ? "Saving..." : "Save"}
      </button>
      {message && <span style={{ marginLeft: 12, fontSize: 14 }}>{message}</span>}
    </form>
  );
}

function SupportContactSection() {
  const [saved, setSaved] = useState<string | null>(null);
  const [email, setEmail] = useState("");
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState<string | null>(null);

  useEffect(() => {
    supabase
      .from("platform_settings")
      .select("support_email")
      .eq("id", true)
      .single()
      .then(({ data }) => {
        const value = (data as { support_email: string } | null)?.support_email ?? "";
        setSaved(value);
        setEmail(value);
      });
  }, []);

  async function save(e: FormEvent) {
    e.preventDefault();
    setSaving(true);
    setMessage(null);
    const { error } = await supabase.rpc("rpc_admin_set_support_email", { p_email: email });
    setSaving(false);
    if (error) {
      setMessage(error.message.includes("support_email_invalid") ? "That does not look like an email address." : error.message);
      return;
    }
    const tidy = email.trim();
    setEmail(tidy);
    setSaved(tidy);
    setMessage("Saved.");
  }

  if (saved === null) return <p className="rc-loading">Loading...</p>;

  return (
    <form onSubmit={save} style={{ marginBottom: 40 }}>
      <h2 className="rc-h2" style={{ marginBottom: 8 }}>Support contact</h2>
      <p style={{ fontSize: 13, color: "var(--rc-text-muted)", marginTop: 0 }}>
        The email clients see on the "Access is paused" screen and in the licence-ending banners, so they know who to ask about renewing. Leave blank to show no email.
      </p>
      <label style={{ display: "block", marginBottom: 16, fontSize: 14 }}>
        Support email
        <input type="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="support@example.com" style={FIELD} />
      </label>
      <button type="submit" disabled={saving || email.trim() === saved} style={{ padding: "8px 16px" }}>
        {saving ? "Saving..." : "Save"}
      </button>
      {message && <span style={{ marginLeft: 12, fontSize: 14 }}>{message}</span>}
    </form>
  );
}

/** Admin > Developer info: the platform owner's own details. The support email clients are shown when their licence runs
 *  out (migration 0050), and the billing details printed on every invoice (migration 0025). */
export function DeveloperInfo() {
  return (
    <div>
      <h1 className="rc-h1" style={{ marginBottom: 20 }}>Developer info</h1>
      <SupportContactSection />
      <BillingSection />
    </div>
  );
}
